import { Close, Download, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButton, CircularProgress } from '@mui/material';
import React, { useState, useCallback } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import { MobileImageViewerProps } from './MobileImageViewer.props';
import {
  ViewerDialog,
  TopBar,
  TopBarInfo,
  FileName,
  ImageCounter,
  BottomBar,
  ImageContainer,
  NavButton,
} from './MobileImageViewer.styles';
import { useImageNavigation } from './useImageNavigation';
import { useGetUploadedFilePresignedUrlById } from '../../../queries/uploadedfiles-query';
import { isSafeUrl } from '../../../utils/common';

export const MobileImageViewer: React.FC<MobileImageViewerProps> = ({
  open,
  images,
  initialIndex = 0,
  onClose,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const { currentIndex, transformRef, goNext, goPrev, handleTouchStart, handleTouchEnd } =
    useImageNavigation({ imageCount: images.length, initialIndex, open });

  const currentImage = images[currentIndex];
  const getDownloadUrl = useGetUploadedFilePresignedUrlById(currentImage?.id ?? 0, 'attachment');

  const handleDownload = useCallback(async (): Promise<void> => {
    if (!currentImage) return;
    setIsDownloading(true);
    try {
      const result = await getDownloadUrl.refetch();
      if (result.data?.url && isSafeUrl(result.data.url)) {
        const link = document.createElement('a');
        link.href = result.data.url;
        link.download = currentImage.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } finally {
      setIsDownloading(false);
    }
  }, [currentImage, getDownloadUrl]);

  if (!currentImage) return null;

  return (
    <ViewerDialog open={open} onClose={onClose} fullScreen aria-label="Image viewer">
      <TopBar>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close image viewer"
          sx={{ color: 'common.white' }}
        >
          <Close />
        </IconButton>
        <TopBarInfo>
          <FileName>{currentImage.fileName}</FileName>
          {images.length > 1 && (
            <ImageCounter>
              {currentIndex + 1} / {images.length}
            </ImageCounter>
          )}
        </TopBarInfo>
      </TopBar>

      {images.length > 1 && currentIndex > 0 && (
        <NavButton onClick={goPrev} sx={{ left: 4 }} aria-label="Previous image" size="small">
          <ChevronLeft />
        </NavButton>
      )}

      {images.length > 1 && currentIndex < images.length - 1 && (
        <NavButton onClick={goNext} sx={{ right: 4 }} aria-label="Next image" size="small">
          <ChevronRight />
        </NavButton>
      )}

      <ImageContainer onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={1}
          maxScale={5}
          doubleClick={{ mode: 'toggle', step: 2 }}
          panning={{ disabled: false }}
          key={currentImage.id}
        >
          <TransformComponent
            wrapperStyle={{ width: '100%', height: '100%' }}
            contentStyle={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={currentImage.url}
              alt={currentImage.fileName}
              style={
                {
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  userSelect: 'none',
                  WebkitUserDrag: 'none',
                } as React.CSSProperties
              }
              draggable={false}
            />
          </TransformComponent>
        </TransformWrapper>
      </ImageContainer>

      <BottomBar>
        <IconButton
          onClick={handleDownload}
          disabled={isDownloading}
          aria-label="Download image"
          sx={{ color: 'common.white' }}
        >
          {isDownloading ? (
            <CircularProgress size={24} sx={{ color: 'common.white' }} />
          ) : (
            <Download />
          )}
        </IconButton>
      </BottomBar>
    </ViewerDialog>
  );
};
